import React from 'react';
import styles from './UI.module.css';

/* ── Input ── */
export function Input({ label, error, hint, required, ...props }) {
  return (
    <div className={styles.field}>
      {label && (
        <label className={styles.label}>
          {label} {required && <span className={styles.req}>*</span>}
        </label>
      )}
      <input className={`${styles.input} ${error ? styles.inputError : ''}`} {...props} />
      {hint && !error && <p className={styles.hint}>{hint}</p>}
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
}

/* ── Textarea ── */
export function Textarea({ label, error, required, rows = 3, ...props }) {
  return (
    <div className={styles.field}>
      {label && (
        <label className={styles.label}>
          {label} {required && <span className={styles.req}>*</span>}
        </label>
      )}
      <textarea className={`${styles.input} ${styles.textarea} ${error ? styles.inputError : ''}`} rows={rows} {...props} />
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
}

/* ── Select ── */
export function Select({ label, error, required, options = [], placeholder, ...props }) {
  return (
    <div className={styles.field}>
      {label && (
        <label className={styles.label}>
          {label} {required && <span className={styles.req}>*</span>}
        </label>
      )}
      <select className={`${styles.input} ${styles.select} ${error ? styles.inputError : ''}`} {...props}>
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
}

/* ── RadioGroup ── */
export function RadioGroup({ label, options = [], value, onChange, name, required, error }) {
  return (
    <div className={styles.field}>
      {label && (
        <label className={styles.label}>
          {label} {required && <span className={styles.req}>*</span>}
        </label>
      )}
      <div className={styles.radioGroup}>
        {options.map((opt) => (
          <label key={opt} className={styles.radioLabel}>
            <input
              type="radio"
              name={name}
              value={opt}
              checked={value === opt}
              onChange={() => onChange(opt)}
              className={styles.radioInput}
            />
            <span className={styles.radioText}>{opt}</span>
          </label>
        ))}
      </div>
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
}

/* ── CheckboxGroup ── */
export function CheckboxGroup({ label, items = [], values = {}, onChange }) {
  return (
    <div className={styles.field}>
      {label && <label className={styles.label}>{label}</label>}
      <div className={styles.checkboxGroup}>
        {items.map(({ key, label: itemLabel }) => (
          <label key={key} className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={!!values[key]}
              onChange={(e) => onChange(key, e.target.checked)}
              className={styles.checkboxInput}
            />
            <span className={styles.checkboxText}>{itemLabel}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

/* ── Rating Row (1-5 scale) ── */
export function RatingRow({ label, value, onChange }) {
  return (
    <div className={styles.ratingRow}>
      <span className={styles.ratingLabel}>{label}</span>
      <div className={styles.ratingDots}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(value === n ? 0 : n)}
            className={`${styles.ratingDot} ${value >= n ? styles.ratingDotFilled : ''}`}
            title={`Rating: ${n}`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── Section Header ── */
export function SectionHeader({ letter, title, subtitle }) {
  return (
    <div className={styles.sectionHeader}>
      <div className={styles.sectionBadge}>{letter}</div>
      <div>
        <h2 className={styles.sectionTitle}>{title}</h2>
        {subtitle && <p className={styles.sectionSubtitle}>{subtitle}</p>}
      </div>
    </div>
  );
}

/* ── Button ── */
export function Button({ children, variant = 'primary', loading, icon, ...props }) {
  return (
    <button className={`${styles.btn} ${styles[`btn_${variant}`]}`} disabled={loading || props.disabled} {...props}>
      {loading ? <span className={styles.spinner} /> : icon}
      {children}
    </button>
  );
}

/* ── Card ── */
export function Card({ children, className = '' }) {
  return <div className={`${styles.card} ${className}`}>{children}</div>;
}

/* ── Badge ── */
export function Badge({ children, color = 'teal' }) {
  return <span className={`${styles.badge} ${styles[`badge_${color}`]}`}>{children}</span>;
}
